import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import { CheckSquare, Square, Trash2, Loader, GripVertical, X, Search, ChevronDown, Plus, Calendar } from 'lucide-react';
import Button from '../components/ui/Button';
import PrioritySelector from '../components/ui/PrioritySelector';
import { useAppStore } from '../store';
import ErrorModal from '../components/modals/ErrorModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useAuth } from '../context/AuthContext';
import { 
  getBuenosAiresDate, 
  getNextBusinessDay, 
  formatDateToString,
  isTodayInBuenosAires
} from '../utils/businessDaysUtils';

interface DragItem {
  index: number;
  id: string;
  type: string;
}

interface ProductionTodo {
  id: string;
  title: string;
  priority: 'high' | 'medium' | 'low' | null;
  completed: boolean;
  supplier_id: string | null;
  supplier_name?: string;
  date: string;
  created_at: string;
  updated_at: string;
}

interface Tab {
  id: string;
  date: string;
  label: string;
  isToday: boolean;
  isPast: boolean;
}

const ProductionTodoList: React.FC = () => {
  const { userRole } = useAuth();
  const { 
    suppliers,
    productionTodos,
    fetchSuppliers,
    fetchProductionTodos,
    addProductionTodo,
    updateProductionTodo,
    deleteProductionTodo,
    toggleProductionTodoCompletion
  } = useAppStore();

  // Estados locales
  const [localLoading, setLocalLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [newTaskText, setNewTaskText] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState<'high' | 'medium' | 'low' | null>(null);
  const [newTaskSupplier, setNewTaskSupplier] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [deleteCompletedConfirmOpen, setDeleteCompletedConfirmOpen] = useState(false);
  const [filterBySupplier, setFilterBySupplier] = useState<string | null>(null);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [supplierDropdownSearch, setSupplierDropdownSearch] = useState('');
  const [showTaskSupplierDropdown, setShowTaskSupplierDropdown] = useState(false);
  const [taskSupplierSearch, setTaskSupplierSearch] = useState('');
  const [activeTab, setActiveTab] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [deleteDayConfirmOpen, setDeleteDayConfirmOpen] = useState(false);
  const [dayToDelete, setDayToDelete] = useState<string | null>(null);

  // Verificar permisos
  if (userRole !== 'Administrador' && userRole !== 'Producción') {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
            Acceso Denegado
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            No tienes permisos para acceder a esta página.
          </p>
        </div>
      </div>
    );
  }

  // Función para formatear fechas de display
  const formatDisplayDate = useCallback((dateString: string): string => {
    try {
      if (!dateString) return 'Fecha inválida';
      
      const date = new Date(dateString + 'T00:00:00');
      if (isNaN(date.getTime())) return 'Fecha inválida';
      
      const today = getBuenosAiresDate();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const normalizeDate = (d: Date) => {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
      };

      const normalizedDate = normalizeDate(date);
      const normalizedToday = normalizeDate(today);
      const normalizedYesterday = normalizeDate(yesterday);
      const normalizedTomorrow = normalizeDate(tomorrow);

      if (normalizedDate.getTime() === normalizedToday.getTime()) {
        return 'Hoy';
      } else if (normalizedDate.getTime() === normalizedYesterday.getTime()) {
        return 'Ayer';
      } else if (normalizedDate.getTime() === normalizedTomorrow.getTime()) {
        return 'Mañana';
      } else {
        return date.toLocaleDateString('es-ES', { 
          weekday: 'short', 
          day: 'numeric', 
          month: 'short' 
        });
      }
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Fecha inválida';
    }
  }, []);

  // Generar tabs automáticamente
  const generateTabs = useCallback((): Tab[] => {
    try {
      const today = getBuenosAiresDate();
      const todayString = formatDateToString(today);
      
      // Obtener todas las fechas únicas de las tareas
      const taskDates = new Set<string>();
      
      // Siempre incluir hoy
      taskDates.add(todayString);
      
      // Agregar fechas de tareas existentes
      if (productionTodos && Array.isArray(productionTodos)) {
        productionTodos.forEach(task => {
          if (task.date && task.date.trim()) {
            taskDates.add(task.date);
          }
        });
      }
      
      // Agregar el día activo si no está ya incluido
      if (activeTab && activeTab.trim()) {
        taskDates.add(activeTab);
      }
      
      // Convertir a array de tabs
      const tabs: Tab[] = Array.from(taskDates)
        .map(dateString => {
          try {
            const date = new Date(dateString + 'T00:00:00');
            if (isNaN(date.getTime())) return null;
            
            const isToday = isTodayInBuenosAires(dateString);
            const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
            
            return {
              id: dateString,
              date: dateString,
              label: formatDisplayDate(dateString),
              isToday,
              isPast
            };
          } catch (error) {
            console.error('Error processing date:', dateString, error);
            return null;
          }
        })
        .filter((tab): tab is Tab => tab !== null)
        .filter(tab => !tab.isPast) // Remover tabs pasados
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      
      return tabs;
    } catch (error) {
      console.error('Error generating tabs:', error);
      // Fallback: solo hoy
      const today = getBuenosAiresDate();
      const todayString = formatDateToString(today);
      return [{
        id: todayString,
        date: todayString,
        label: 'Hoy',
        isToday: true,
        isPast: false
      }];
    }
  }, [productionTodos, activeTab, formatDisplayDate]);

  const tabs = generateTabs();

  // Inicialización única
  useEffect(() => {
    if (isInitialized) return;
    
    const initializeData = async () => {
      try {
        setLocalLoading(true);
        await Promise.all([
          fetchSuppliers(),
          fetchProductionTodos()
        ]);
        setIsInitialized(true);
      } catch (error: any) {
        console.error('Error loading initial data:', error);
        setError(error.message || 'Error al cargar los datos');
        setShowErrorModal(true);
      } finally {
        setLocalLoading(false);
      }
    };

    initializeData();
  }, [isInitialized, fetchSuppliers, fetchProductionTodos]);

  // Establecer tab activo cuando se cargan los datos
  useEffect(() => {
    if (isInitialized && tabs.length > 0 && !activeTab) {
      const today = getBuenosAiresDate();
      const todayString = formatDateToString(today);
      const todayTab = tabs.find(tab => tab.date === todayString);
      setActiveTab(todayTab ? todayString : tabs[0].date);
    }
  }, [isInitialized, tabs, activeTab]);

  // Auto-limpieza de tareas pasadas
  useEffect(() => {
    if (!isInitialized || !productionTodos || productionTodos.length === 0) return;
    
    const cleanupPastTasks = async () => {
      try {
        const today = getBuenosAiresDate();
        
        // Encontrar tareas de días pasados que no están completadas
        const pastIncompleteTasks = productionTodos.filter(task => {
          if (!task.date) return false;
          try {
            const taskDate = new Date(task.date + 'T00:00:00');
            if (isNaN(taskDate.getTime())) return false;
            const isPast = taskDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
            return isPast && !task.completed;
          } catch (error) {
            console.error('Error processing task date:', task.date, error);
            return false;
          }
        });

        if (pastIncompleteTasks.length > 0) {
          console.log(`Moving ${pastIncompleteTasks.length} incomplete tasks from past days`);
          
          // Mover cada tarea incompleta al siguiente día hábil
          for (const task of pastIncompleteTasks) {
            try {
              const taskDate = new Date(task.date + 'T00:00:00');
              const nextBusinessDay = getNextBusinessDay(taskDate);
              const nextBusinessDayString = formatDateToString(nextBusinessDay);
              
              await updateProductionTodo(task.id, { date: nextBusinessDayString });
              console.log(`Moved task "${task.title}" from ${task.date} to ${nextBusinessDayString}`);
            } catch (error) {
              console.error(`Error moving task ${task.id}:`, error);
            }
          }
        }
      } catch (error) {
        console.error('Error in cleanupPastTasks:', error);
      }
    };

    cleanupPastTasks();
  }, [isInitialized, productionTodos, updateProductionTodo]);

  // Memoized values
  const suppliersWithTasks = useMemo(() => {
    if (!suppliers || !Array.isArray(suppliers)) return [];
    return suppliers.filter(supplier => 
      productionTodos.some(task => task.supplier_id === supplier.id && !task.completed)
    );
  }, [suppliers, productionTodos]);

  const filteredSuppliersForFilter = useMemo(() => {
    return suppliersWithTasks.filter(supplier =>
      supplier.name.toLowerCase().includes(supplierDropdownSearch.toLowerCase())
    );
  }, [suppliersWithTasks, supplierDropdownSearch]);

  const filteredSuppliersForTask = useMemo(() => {
    if (!suppliers || !Array.isArray(suppliers)) return [];
    return suppliers.filter(supplier =>
      supplier.name.toLowerCase().includes(taskSupplierSearch.toLowerCase())
    );
  }, [suppliers, taskSupplierSearch]);

  const tasksForActiveTab = useMemo(() => {
    if (!activeTab || !productionTodos) return [];
    return productionTodos.filter(task => task.date === activeTab);
  }, [activeTab, productionTodos]);

  const filteredTasks = useMemo(() => {
    if (!filterBySupplier) return tasksForActiveTab;
    return tasksForActiveTab.filter(task => task.supplier_id === filterBySupplier);
  }, [filterBySupplier, tasksForActiveTab]);

  const completedTasksCount = useMemo(() => {
    return tasksForActiveTab.filter(task => task.completed).length;
  }, [tasksForActiveTab]);

  // Funciones de manejo
  const handleAddTask = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newTaskText.trim() && activeTab) {
      try {
        await addProductionTodo({
          title: newTaskText.trim(),
          priority: newTaskPriority,
          supplier_id: newTaskSupplier,
          date: activeTab,
          completed: false
        });
        
        setNewTaskText('');
        setNewTaskPriority(null);
        setNewTaskSupplier(null);
        setTaskSupplierSearch('');
      } catch (error: any) {
        console.error('Error adding task:', error);
        setError(error.message || 'Error al agregar la tarea');
        setShowErrorModal(true);
      }
    }
  };

  const handleToggleComplete = async (id: string, completed: boolean) => {
    try {
      await toggleProductionTodoCompletion(id, !completed);
    } catch (error: any) {
      console.error('Error toggling task completion:', error);
      setError(error.message || 'Error al actualizar el estado de la tarea');
      setShowErrorModal(true);
    }
  };

  const handleDeleteTask = (id: string) => {
    setTaskToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    
    try {
      await deleteProductionTodo(taskToDelete);
      setTaskToDelete(null);
      setDeleteConfirmOpen(false);
    } catch (error: any) {
      console.error('Error deleting task:', error);
      setError(error.message || 'Error al eliminar la tarea');
      setShowErrorModal(true);
    }
  };

  const handlePriorityChange = async (taskId: string, newPriority: 'high' | 'medium' | 'low' | null) => {
    try {
      await updateProductionTodo(taskId, { priority: newPriority });
    } catch (error: any) {
      console.error('Error updating task priority:', error);
      setError(error.message || 'Error al actualizar la prioridad de la tarea');
      setShowErrorModal(true);
    }
  };

  const moveTaskToDate = async (taskId: string, targetDate: string) => {
    try {
      if (!targetDate) return;
      await updateProductionTodo(taskId, { date: targetDate });
    } catch (error: any) {
      console.error('Error moving task to date:', error);
      setError(error.message || 'Error al mover la tarea');
      setShowErrorModal(true);
    }
  };

  const moveTaskToTomorrow = async (taskId: string, currentDate: string) => {
    try {
      const base = new Date(currentDate + 'T00:00:00');
      const tomorrow = new Date(base);
      tomorrow.setDate(base.getDate() + 1);
      const y = tomorrow.getFullYear();
      const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
      const d = String(tomorrow.getDate()).padStart(2, '0');
      const dateStr = `${y}-${m}-${d}`;
      
      await moveTaskToDate(taskId, dateStr);
    } catch (error: any) {
      console.error('Error moving task to tomorrow:', error);
      setError(error.message || 'Error al mover la tarea a mañana');
      setShowErrorModal(true);
    }
  };

  const handleDeleteCompletedTasks = () => {
    if (completedTasksCount > 0) {
      setDeleteCompletedConfirmOpen(true);
    }
  };

  const confirmDeleteCompletedTasks = async () => {
    try {
      const completedTaskIds = tasksForActiveTab
        .filter(task => task.completed)
        .map(task => task.id);
      
      for (const id of completedTaskIds) {
        await deleteProductionTodo(id);
      }
      setDeleteCompletedConfirmOpen(false);
    } catch (error: any) {
      console.error('Error deleting completed tasks:', error);
      setError(error.message || 'Error al eliminar las tareas completadas');
      setShowErrorModal(true);
    }
  };

  const handleAddNewDay = () => {
    setShowDatePicker(true);
    // Establecer fecha por defecto como mañana
    const tomorrow = new Date(getBuenosAiresDate());
    tomorrow.setDate(tomorrow.getDate() + 1);
    const y = tomorrow.getFullYear();
    const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const d = String(tomorrow.getDate()).padStart(2, '0');
    setSelectedDate(`${y}-${m}-${d}`);
  };

  const confirmAddNewDay = () => {
    if (selectedDate) {
      // Simplemente establecer el tab activo - el tab se creará automáticamente
      // cuando se agregue la primera tarea a ese día
      setActiveTab(selectedDate);
      setShowDatePicker(false);
      setSelectedDate('');
    }
  };

  const cancelAddNewDay = () => {
    setShowDatePicker(false);
    setSelectedDate('');
  };

  const handleDeleteDay = (date: string) => {
    setDayToDelete(date);
    setDeleteDayConfirmOpen(true);
  };

  const confirmDeleteDay = async () => {
    if (!dayToDelete) return;
    
    try {
      // Obtener todas las tareas del día
      const tasksToDelete = productionTodos.filter(task => task.date === dayToDelete);
      
      // Eliminar todas las tareas del día
      for (const task of tasksToDelete) {
        await deleteProductionTodo(task.id);
      }
      
      // Si el día eliminado era el activo, cambiar a otro día
      if (activeTab === dayToDelete) {
        const remainingTabs = tabs.filter(tab => tab.date !== dayToDelete);
        if (remainingTabs.length > 0) {
          setActiveTab(remainingTabs[0].date);
        }
      }
      
      setDayToDelete(null);
      setDeleteDayConfirmOpen(false);
    } catch (error: any) {
      console.error('Error deleting day:', error);
      setError(error.message || 'Error al eliminar el día');
      setShowErrorModal(true);
    }
  };

  // Cerrar dropdowns al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Element;
      
      if (showSupplierDropdown && !target.closest('.supplier-dropdown')) {
        setShowSupplierDropdown(false);
        setSupplierDropdownSearch('');
      }
      
      if (showTaskSupplierDropdown && !target.closest('.task-supplier-dropdown')) {
        setShowTaskSupplierDropdown(false);
        setTaskSupplierSearch('');
      }

      if (showDatePicker && target.classList.contains('fixed')) {
        cancelAddNewDay();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSupplierDropdown, showTaskSupplierDropdown, showDatePicker]);

  // Definir tipo de item para drag and drop
  const ITEM_TYPE = 'PRODUCTION_TASK';

  // Componente de tarea con drag and drop
  const TaskItem = ({ task, index }: { task: ProductionTodo, index: number }) => {
    const ref = React.useRef<HTMLLIElement>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(task.title);
    
    const [{ isDragging }, drag] = useDrag({
      type: ITEM_TYPE,
      item: { index, id: task.id, type: ITEM_TYPE },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });
    
    const [{ isOver, canDrop }, drop] = useDrop({
      accept: ITEM_TYPE,
      hover: (item: DragItem, monitor) => {
        if (!ref.current) {
          return;
        }
        
        const dragIndex = item.index;
        const hoverIndex = index;
        
        if (dragIndex === hoverIndex) {
          return;
        }

        const hoverBoundingRect = ref.current.getBoundingClientRect();
        const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        if (!clientOffset) return;
        
        const hoverClientY = clientOffset.y - hoverBoundingRect.top;
        
        if (
          (dragIndex < hoverIndex && hoverClientY > hoverMiddleY) ||
          (dragIndex > hoverIndex && hoverClientY < hoverMiddleY)
        ) {
          item.index = hoverIndex;
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    });
    
    drag(drop(ref));
    
    let bgColorClass = task.completed ? 'bg-gray-50 dark:bg-gray-800/50' : '';
    
    if (isDragging) {
      bgColorClass = 'opacity-50 bg-gray-100 dark:bg-gray-700';
    } else if (isOver && canDrop) {
      bgColorClass = 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800';
    }
    
    const handleSaveTitle = async () => {
      const trimmed = editText.trim();
      if (!trimmed || trimmed === task.title) {
        setIsEditing(false);
        setEditText(task.title);
        return;
      }
      try {
        await updateProductionTodo(task.id, { title: trimmed });
        setIsEditing(false);
      } catch (error: any) {
        console.error('Error updating task title:', error);
        setError(error.message || 'Error al actualizar la tarea');
        setShowErrorModal(true);
      }
    };

    return (
      <li 
        ref={ref}
        className={`flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 group transition-colors ${bgColorClass} cursor-move`}
      >
        <div className="flex items-center flex-1">
          <div className="mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 touch-manipulation">
            <GripVertical className="h-5 w-5" />
          </div>
          <button 
            onClick={() => handleToggleComplete(task.id, task.completed)}
            className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 focus:outline-none mr-3"
          >
            {task.completed ? (
              <CheckSquare className="h-5 w-5 text-green-600 dark:text-green-400" />
            ) : (
              <Square className="h-5 w-5" />
            )}
          </button>
          
          <div className="flex-1">
            {isEditing ? (
              <input
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => { 
                  if (e.key === 'Enter') handleSaveTitle(); 
                  if (e.key === 'Escape') { 
                    setIsEditing(false); 
                    setEditText(task.title); 
                  } 
                }}
                onBlur={handleSaveTitle}
                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                autoFocus
              />
            ) : (
              <button
                className={`text-left ${task.completed ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}`}
                onClick={() => setIsEditing(true)}
                title="Editar tarea"
              >
                {task.priority === 'high' && '🔴 '}
                {task.priority === 'medium' && '🟡 '}
                {task.priority === 'low' && '🟢 '}
                {task.title}
              </button>
            )}
            {task.supplier_name && (
              <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mt-1">
                <span>Proveedor: {task.supplier_name}</span>
                <button
                  onClick={() => setFilterBySupplier(task.supplier_id)}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    setFilterBySupplier(task.supplier_id);
                  }}
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 active:text-indigo-800 dark:active:text-indigo-200 transition-colors touch-manipulation select-none p-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 active:bg-indigo-100 dark:active:bg-indigo-900/30"
                  title="Filtrar por este proveedor"
                  style={{ minHeight: '32px', minWidth: '32px' }}
                >
                  <Search className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
          
          <div className="mr-3">
            <PrioritySelector
              value={task.priority}
              onChange={(priority) => handlePriorityChange(task.id, priority)}
            />
          </div>

          {/* Botón Mañana */}
          <div className="flex items-center gap-2 mr-2">
            <button
              onClick={() => moveTaskToTomorrow(task.id, task.date)}
              className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 transition-colors"
              title="Mover a mañana"
            >
              Mañana
            </button>
          </div>
          
          <button
            onClick={() => handleDeleteTask(task.id)}
            className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 focus:outline-none opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </li>
    );
  };

  if (localLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="h-8 w-8 text-indigo-600 dark:text-indigo-400 animate-spin mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Cargando to-do de producción...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ touchAction: 'manipulation' }}>
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">To-Do de Producción</h2>
          
          <div className="flex items-center space-x-4">
            {/* Filtro por proveedor */}
            <div className="relative supplier-dropdown">
              <button
                onClick={() => setShowSupplierDropdown(!showSupplierDropdown)}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  setShowSupplierDropdown(!showSupplierDropdown);
                }}
                className="flex items-center space-x-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600 transition-colors touch-manipulation select-none"
                style={{ minHeight: '44px' }}
              >
                <Search className="h-4 w-4" />
                <span className="text-sm">
                  {filterBySupplier 
                    ? suppliers.find(s => s.id === filterBySupplier)?.name || 'Proveedor'
                    : 'Filtrar por proveedor'
                  }
                </span>
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {showSupplierDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-50">
                  <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Buscar proveedor..."
                        value={supplierDropdownSearch}
                        onChange={(e) => setSupplierDropdownSearch(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        autoFocus
                      />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    <button
                      onClick={() => {
                        setFilterBySupplier(null);
                        setShowSupplierDropdown(false);
                        setSupplierDropdownSearch('');
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        setFilterBySupplier(null);
                        setShowSupplierDropdown(false);
                        setSupplierDropdownSearch('');
                      }}
                      className={`w-full text-left px-3 py-3 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 touch-manipulation select-none ${
                        !filterBySupplier ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'
                      }`}
                      style={{ minHeight: '44px' }}
                    >
                      Todas las tareas
                    </button>
                    {filteredSuppliersForFilter.map(supplier => (
                      <button
                        key={supplier.id}
                        onClick={() => {
                          setFilterBySupplier(supplier.id);
                          setShowSupplierDropdown(false);
                          setSupplierDropdownSearch('');
                        }}
                        onTouchEnd={(e) => {
                          e.preventDefault();
                          setFilterBySupplier(supplier.id);
                          setShowSupplierDropdown(false);
                          setSupplierDropdownSearch('');
                        }}
                        className={`w-full text-left px-3 py-3 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 touch-manipulation select-none ${
                          filterBySupplier === supplier.id ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'
                        }`}
                        style={{ minHeight: '44px' }}
                      >
                        {supplier.name}
                      </button>
                    ))}
                    {filteredSuppliersForFilter.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                        No hay proveedores con tareas activas
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            
            {completedTasksCount > 0 && (
              <Button
                variant="danger"
                size="sm"
                onClick={handleDeleteCompletedTasks}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleDeleteCompletedTasks();
                }}
                className="touch-manipulation select-none"
                style={{ minHeight: '44px' }}
              >
                Eliminar completadas ({completedTasksCount})
              </Button>
            )}
          </div>
        </div>
        
        {/* Leyenda de prioridades */}
        <div className="flex items-center space-x-6 text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 bg-red-500 rounded-full"></span>
            <span>Alta prioridad</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 bg-yellow-400 rounded-full"></span>
            <span>Media prioridad</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 bg-green-500 rounded-full"></span>
            <span>Baja prioridad</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="w-3 h-3 bg-transparent border border-gray-300 rounded-full"></span>
            <span>Sin prioridad</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Card>
        <CardHeader title="Días" />
        <CardContent>
          <div className="space-y-4">
            {/* Botón para agregar nuevo día */}
            <div className="flex justify-between items-center">
              <div className="flex flex-wrap gap-2">
                {tabs.map((tab) => {
                  const tasksInTab = productionTodos.filter(task => task.date === tab.date);
                  const hasTasks = tasksInTab.length > 0;
                  
                  return (
                    <div
                      key={tab.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors group ${
                        activeTab === tab.id
                          ? 'bg-indigo-100 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300'
                          : 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                      onClick={() => setActiveTab(tab.id)}
                      onDragOver={(e) => {
                        e.preventDefault();
                      }}
                      onDrop={(e) => {
                        try {
                          const taskId = e.dataTransfer.getData('text/plain');
                          if (taskId) moveTaskToDate(taskId, tab.date);
                        } catch {}
                      }}
                    >
                      <span className="text-sm font-medium">{tab.label}</span>
                      {hasTasks && (
                        <span className="text-xs bg-gray-400 dark:bg-gray-500 text-white rounded-full px-2 py-1">
                          {tasksInTab.length}
                        </span>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDay(tab.date);
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-600 dark:hover:text-red-400 ml-1"
                        title={`Eliminar día ${tab.label}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
              
              <Button
                variant="secondary"
                size="sm"
                onClick={handleAddNewDay}
                className="touch-manipulation select-none"
                style={{ minHeight: '44px' }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuevo día
              </Button>
            </div>

            {/* Date Picker Modal */}
            {showDatePicker && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Seleccionar fecha para nuevo día
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Fecha
                      </label>
                      <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white bg-white text-gray-900"
                        min={formatDateToString(getBuenosAiresDate())}
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-3">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={cancelAddNewDay}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={confirmAddNewDay}
                        disabled={!selectedDate}
                      >
                        <Calendar className="h-4 w-4 mr-2" />
                        Crear día
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title={`Tareas - ${tabs.find(tab => tab.id === activeTab)?.label || 'Seleccionar día'}`} />
        <CardContent>
          <div className="space-y-2">
            {/* Input para nueva tarea */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-4 mb-4">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  placeholder="Agregar nueva tarea... (Enter para guardar)"
                  value={newTaskText}
                  onChange={(e) => setNewTaskText(e.target.value)}
                  onKeyDown={handleAddTask}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white bg-white text-gray-900 placeholder-gray-400 dark:placeholder-gray-500"
                />
                <PrioritySelector
                  value={newTaskPriority}
                  onChange={setNewTaskPriority}
                />
                <div className="relative min-w-[150px] task-supplier-dropdown">
                  <button
                    onClick={() => setShowTaskSupplierDropdown(!showTaskSupplierDropdown)}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      setShowTaskSupplierDropdown(!showTaskSupplierDropdown);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-left flex items-center justify-between touch-manipulation select-none"
                    style={{ minHeight: '44px' }}
                  >
                    <span className="truncate">
                      {newTaskSupplier 
                        ? suppliers.find(s => s.id === newTaskSupplier)?.name || 'Proveedor'
                        : 'Seleccionar proveedor'
                      }
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
                  </button>
                  
                  {showTaskSupplierDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg z-50">
                      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Buscar proveedor..."
                            value={taskSupplierSearch}
                            onChange={(e) => setTaskSupplierSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setShowTaskSupplierDropdown(false);
                                setTaskSupplierSearch('');
                              }
                            }}
                            className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        <button
                          onClick={() => {
                            setNewTaskSupplier(null);
                            setShowTaskSupplierDropdown(false);
                            setTaskSupplierSearch('');
                          }}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            setNewTaskSupplier(null);
                            setShowTaskSupplierDropdown(false);
                            setTaskSupplierSearch('');
                          }}
                          className={`w-full text-left px-3 py-3 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 touch-manipulation select-none ${
                            !newTaskSupplier ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'
                          }`}
                          style={{ minHeight: '44px' }}
                        >
                          Sin proveedor
                        </button>
                        {filteredSuppliersForTask.map(supplier => (
                          <button
                            key={supplier.id}
                            onClick={() => {
                              setNewTaskSupplier(supplier.id);
                              setShowTaskSupplierDropdown(false);
                              setTaskSupplierSearch('');
                            }}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              setNewTaskSupplier(supplier.id);
                              setShowTaskSupplierDropdown(false);
                              setTaskSupplierSearch('');
                            }}
                            className={`w-full text-left px-3 py-3 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 touch-manipulation select-none ${
                              newTaskSupplier === supplier.id ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'
                            }`}
                            style={{ minHeight: '44px' }}
                          >
                            {supplier.name}
                          </button>
                        ))}
                        {filteredSuppliersForTask.length === 0 && taskSupplierSearch && (
                          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
                            <Search className="h-4 w-4 mx-auto mb-1 opacity-50" />
                            No se encontraron proveedores
                          </div>
                        )}
                        {filteredSuppliersForTask.length === 0 && !taskSupplierSearch && (
                          <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
                            No hay proveedores disponibles
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Indicador de filtro */}
            {filterBySupplier && (
              <div className="flex items-center justify-between mb-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-md">
                <div className="flex items-center space-x-2">
                  <Search className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  <span className="text-sm text-indigo-700 dark:text-indigo-300">
                    Filtrando por: {suppliers.find(s => s.id === filterBySupplier)?.name || 'Proveedor desconocido'} 
                    ({filteredTasks.length} tarea{filteredTasks.length !== 1 ? 's' : ''})
                  </span>
                </div>
                <button
                  onClick={() => setFilterBySupplier(null)}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    setFilterBySupplier(null);
                  }}
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 active:text-indigo-800 dark:active:text-indigo-200 transition-colors touch-manipulation select-none p-2 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 active:bg-indigo-100 dark:active:bg-indigo-900/30"
                  title="Limpiar filtro"
                  style={{ minHeight: '44px', minWidth: '44px' }}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Lista de tareas */}
            {filteredTasks.length > 0 ? (
              <DndProvider backend={HTML5Backend}>
                <ul className="space-y-2">
                  {filteredTasks.map((task, index) => (
                    <div
                      key={task.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', task.id);
                      }}
                      onDragEnd={(e) => {
                        // noop
                      }}
                    >
                      <TaskItem task={task} index={index} />
                    </div>
                  ))}
                </ul>
              </DndProvider>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  {filterBySupplier 
                    ? `No hay tareas para el proveedor seleccionado en este día`
                    : 'No hay tareas pendientes para este día'
                  }
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Escribe una tarea y presiona Enter para agregarla
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modales de confirmación */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setTaskToDelete(null);
        }}
        onConfirm={confirmDeleteTask}
        title="Eliminar Tarea"
        message="¿Estás seguro de que deseas eliminar esta tarea? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />

      <ConfirmModal
        isOpen={deleteCompletedConfirmOpen}
        onClose={() => setDeleteCompletedConfirmOpen(false)}
        onConfirm={confirmDeleteCompletedTasks}
        title="Eliminar Tareas Completadas"
        message={`¿Estás seguro de que deseas eliminar todas las tareas completadas (${completedTasksCount})? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
      />

      {/* Delete Day Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteDayConfirmOpen}
        onClose={() => {
          setDeleteDayConfirmOpen(false);
          setDayToDelete(null);
        }}
        onConfirm={confirmDeleteDay}
        title="Eliminar Día"
        message={
          dayToDelete ? 
            `¿Estás seguro de que deseas eliminar el día "${formatDisplayDate(dayToDelete)}"? Se eliminarán todas las tareas de este día (${productionTodos.filter(task => task.date === dayToDelete).length} tareas). Esta acción no se puede deshacer.` :
            '¿Estás seguro de que deseas eliminar este día?'
        }
        confirmText="Eliminar día"
        variant="danger"
      />

      {/* Modal de error */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setError(null);
        }}
        message={error || 'Ha ocurrido un error inesperado'}
      />
    </div>
  );
};

export default ProductionTodoList;